import { ModuleApiProvider, useAuth, useModuleApiContext } from '@berky/auth'
import type {
  ModuleSurfaceDefinition,
  ModuleSurfaceRequest,
} from '@berky/module-contract'
import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  LoadingSkeleton,
} from '@berky/ui'
import { createElement, lazy, Suspense, useState } from 'react'
import { useBeforeUnload, useBlocker } from 'react-router'
import { modules, moduleReferences } from '../modules'
import { moduleAccess } from '../module-access'
import { ModuleErrorBoundary } from './module-error-boundary'

const components = new WeakMap<ModuleSurfaceDefinition, ReturnType<typeof lazy>>()

function componentFor(definition: ModuleSurfaceDefinition) {
  let component = components.get(definition)
  if (!component) {
    component = lazy(definition.load)
    components.set(definition, component)
  }
  return component
}

export function ShellModuleSurface(props: ModuleSurfaceRequest) {
  const sourceModule = useModuleApiContext()
  const { user, isLoading } = useAuth()
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  const target = modules.find(
    (module) => module.id === props.moduleId && module.enabled !== false,
  )
  const definition = target?.surfaces?.[props.surfaceId]
  const source = modules.find((module) => module.id === sourceModule.id)
  const canOpen =
    !!user &&
    !!source &&
    moduleAccess(user.roles, source) === 'erlaubt' &&
    !!target &&
    (definition?.access === 'contextual' ||
      moduleAccess(user.roles, target) === 'erlaubt')
  function close() {
    if (busy) return
    if (dirty) setConfirmClose(true)
    else props.onOpenChange?.(false)
  }
  const content = isLoading ? (
    <LoadingSkeleton variant="form" rows={4} />
  ) : !definition || !target ? (
    <p role="status">Dieser Bereich ist zurzeit nicht verfügbar.</p>
  ) : !canOpen ? (
    <p role="alert">Sie haben keinen Zugriff auf diesen Bereich.</p>
  ) : (
    <ModuleApiProvider
      module={{
        id: target.id,
        apiBasePath: target.apiBasePath,
        availableModules: moduleReferences,
      }}
    >
      <ModuleErrorBoundary
        key={`${target.id}:${props.surfaceId}:${props.recordId}`}
        moduleId={target.id}
        moduleTitle={definition.title}
      >
        <Suspense fallback={<LoadingSkeleton variant="form" rows={4} />}>
          {createElement(componentFor(definition), {
            source: {
              moduleId: sourceModule.id,
              recordType: props.recordType,
              recordId: props.recordId,
            },
            onDirtyChange: setDirty,
            onBusyChange: setBusy,
            onCompleted: props.onCompleted,
          })}
        </Suspense>
      </ModuleErrorBoundary>
    </ModuleApiProvider>
  )
  if (props.presentation !== 'dialog') return content
  return (
    <>
      {(props.open ?? false) && <SurfaceLeaveGuard dirty={dirty} busy={busy} />}
      <Dialog
        open={props.open ?? false}
        onOpenChange={(open) => (open ? props.onOpenChange?.(true) : close())}
      >
        <DialogContent
          className="flex h-[85dvh] w-[92vw] max-w-[92vw] flex-col sm:w-[80vw] sm:max-w-[80vw]"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{definition?.title ?? 'Erfassung'}</DialogTitle>
            <DialogDescription>
              Geöffnet in {source?.title ?? sourceModule.id}. Ihr Vorgang bleibt im
              Hintergrund erhalten.
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto">{content}</div>
          <Button variant="outline" disabled={busy} onClick={close}>
            Zurück zu {source?.title ?? 'Ihrem Vorgang'}
          </Button>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={confirmClose}
        onOpenChange={setConfirmClose}
        title="Ungespeicherte Änderungen verwerfen?"
        description="Speichern Sie Ihre Eingaben, bevor Sie die Erfassung schließen."
        confirmLabel="Änderungen verwerfen"
        cancelLabel="Weiter bearbeiten"
        destructive
        onConfirm={() => {
          setDirty(false)
          setConfirmClose(false)
          props.onOpenChange?.(false)
        }}
      />
    </>
  )
}

function SurfaceLeaveGuard({ dirty, busy }: { dirty: boolean; busy: boolean }) {
  useBeforeUnload((event) => {
    if (dirty || busy) {
      event.preventDefault()
      event.returnValue = ''
    }
  })
  const blocker = useBlocker(dirty || busy)
  return (
    <ConfirmDialog
      open={blocker.state === 'blocked' && !busy}
      onOpenChange={(open) => {
        if (!open && blocker.state === 'blocked') blocker.reset()
      }}
      title="Erfassung verlassen?"
      description="Ungespeicherte Eingaben gehen beim Verlassen verloren."
      confirmLabel="Erfassung verlassen"
      cancelLabel="Weiter bearbeiten"
      destructive
      onConfirm={() => {
        if (blocker.state === 'blocked' && !busy) blocker.proceed()
      }}
    />
  )
}
